import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step, Styles } from 'react-joyride'
import a11yChecker from 'a11y-checker'
import { useDispatch } from 'react-redux'
import { useMount, useSetState } from 'react-use'
import { changeHomeGuide } from 'store/modules/showGuide/action'
import { darkTheme, lightTheme } from 'styles/themes'
import ContentGuide from '../ContentGuide'

import imageStep1 from '../../../../assets/steps/step1.png'
import imageStep2 from '../../../../assets/steps/step2.png'
import imageStep3 from '../../../../assets/steps/step3.png'

interface State {
  run: boolean
  sidebarOpen: boolean
  stepIndex: number
  steps: Step[]
}

interface IJoyrideHome {
  isDarkMode: boolean
  isMobile: boolean
}

const JoyrideHome: React.FC<IJoyrideHome> = ({ isDarkMode, isMobile }) => {
  const dispatch = useDispatch()

  const [{ run, sidebarOpen, stepIndex, steps }, setState] = useSetState<State>({
    run: false,
    sidebarOpen: false,
    stepIndex: 0,
    steps: [],
  })

  const locale = {
    skip: <u aria-label="skip">Pular</u>,
    next: 'Próximo',
    last: 'Começar',
  }

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data

    if (
      ([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status) ||
      action === ACTIONS.CLOSE
    ) {
      // Need to set our running state to false, so we can restart if we click start again.
      setState({ run: false, stepIndex: 0 })
      dispatch(changeHomeGuide(false))
    } else if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1)

      if (sidebarOpen && index === 0) {
        setTimeout(() => {
          setState({ run: true })
        }, 400)
      } else if (sidebarOpen && index === 1) {
        setState({
          run: false,
          sidebarOpen: false,
          stepIndex: nextStepIndex,
        })

        setTimeout(() => {
          setState({ run: true })
        }, 400)
      } else if (index === 2 && action === ACTIONS.PREV) {
        setState({
          run: false,
          sidebarOpen: true,
          stepIndex: nextStepIndex,
        })

        setTimeout(() => {
          setState({ run: true })
        }, 400)
      } else {
        // Update state to advance the tour
        setState({
          sidebarOpen: false,
          stepIndex: nextStepIndex,
        })
      }
    }
  }

  const joyRideStyle: Styles = {
    buttonNext: {
      backgroundColor: isDarkMode
        ? `rgb(${darkTheme.primary.base})`
        : `rgb(${lightTheme.primary.base})`,
      color: isDarkMode ? `rgb(${darkTheme.background.low})` : `rgb(${lightTheme.background.low})`,
      padding: '14px 21px',
      display: 'block',
    },
    options: {
      width: 400,
      zIndex: 1000,
      backgroundColor: isDarkMode
        ? `rgb(${darkTheme.background.low})`
        : `rgb(${lightTheme.background.low})`,
      textColor: isDarkMode
        ? `rgb(${darkTheme.grayscale[100]})`
        : `rgb(${lightTheme.grayscale[100]})`,
    },
    tooltipFooter: {
      flexDirection: 'column-reverse',
      gap: '1rem',
    },
  }

  useMount(() => {
    setState({
      run: true,
      steps: [
        {
          content: (
            <ContentGuide
              step={1}
              image={imageStep1}
              stepsIsOrange
              title="Bem vindo(a) à área do aluno do Direção Concursos!"
              text="Conteúdos atualizados e revisados, com alta incidência em provas dos concursos relacionados."
              isMobile={isMobile}
              isDarkMode={isDarkMode}
            />
          ),
          locale: locale,
          hideBackButton: true,
          showProgress: false,
          placement: 'center',
          target: 'html',
        },
        {
          content: (
            <ContentGuide
              step={2}
              image={imageStep2}
              stepsIsOrange
              title="Acesso rápido e fácil a qualquer momento"
              text="Do seu computador ou dipositivo móvel, assista às aulas ministradas por professores qualificados a qualquer hora ou lugar."
              isMobile={isMobile}
              isDarkMode={isDarkMode}
            />
          ),
          locale: locale,
          hideBackButton: true,
          showProgress: false,
          placement: 'center',
          target: 'html',
        },
        {
          content: (
            <ContentGuide
              step={3}
              image={imageStep3}
              stepsIsOrange
              title="Seja o(a) próximo(a) aprovado(a)!"
              text="O Direção Concursos possui alto índice de alunos aprovados em diversas provas. Pronto(a) para iniciar seus estudos rumo à aprovação?"
              isMobile={isMobile}
              isDarkMode={isDarkMode}
            />
          ),
          locale: locale,
          hideBackButton: true,
          showProgress: false,
          placement: 'center',
          target: 'html',
        },
      ],
    })

    a11yChecker()
  })

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      run={run}
      scrollToFirstStep
      showProgress
      showSkipButton
      stepIndex={stepIndex}
      steps={steps}
      styles={joyRideStyle}
    />
  )
}

export default JoyrideHome
